import { computed, unref } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { useTranslation } from "@/plugins/i18next/i18next";
import gql from "fraql";
import { setupVuelidateRules } from "@/utils/form_validation_utilities";
export function useFormValidation(initForm, formTracker, options) {
    const { t } = useTranslation();
    const { formTrackerName = "form", mapToModel = {}, customDefaultTranslationLabels = undefined, customTranslationErrorLabels = undefined, legacy = {} } = options !== null && options !== void 0 ? options : {};
    function mutateFieldName(fieldName) {
        if (mapToModel && mapToModel[fieldName]) {
            fieldName = mapToModel[fieldName];
        }
        return fieldName;
    }
    const rules = computed(() => {
        return setupVuelidateRules(unref(initForm), formTracker, {
            customTranslationErrorLabels,
            formTrackerName,
            mapToModel,
            t
        });
    });
    const v$ = useVuelidate(rules, { [formTrackerName]: formTracker });
    /**
     * Label and hints for each field that originates from server
     */
    const fieldTranslations = computed(() => {
        var _a;
        return (_a = unref(initForm).formFields) === null || _a === void 0 ? void 0 : _a.reduce((labels, ff) => {
            const labelFieldName = mutateFieldName(ff.name);
            labels[labelFieldName] = {
                label: ff.labelTranslationKey
                    ? t(ff.labelTranslationKey, ff.label)
                    : null,
                hint: ff.hintTranslationKey ? t(ff.hintTranslationKey, ff.hint) : null
            };
            return labels;
        }, {});
    });
    function fieldDisabled(fieldName) {
        var _a;
        return ((_a = unref(initForm).readOnlyFields) !== null && _a !== void 0 ? _a : []).includes(fieldName);
    }
    function fieldErrorMessages(fieldName) {
        const validation = v$.value[formTrackerName][fieldName];
        if (validation.$invalid == false || validation.$dirty == false) {
            return [];
        }
        return validation.$errors.map(x => x.$message);
    }
    /**
     * Validate whole form
     * @returns true if form valid. Otherwise false
     */
    function formValidate() {
        v$.value[formTrackerName].$touch();
        return !v$.value[formTrackerName].$invalid;
    }
    /**
     * Validate whole form but take account of async validations
     * @returns true if form valid. Otherwise false
     */
    async function asyncFormValidate() {
        await v$.value[formTrackerName].$validate();
        return !v$.value[formTrackerName].$invalid;
    }
    function formErrorMessages() {
        return Object.keys(unref(initForm).vuelidateValidations).reduce((errors, fieldName) => {
            var _a, _b;
            fieldName = mutateFieldName(fieldName);
            const v = v$.value[formTrackerName][fieldName];
            const fieldErrors = v.$errors.map(x => x.$message);
            if (fieldErrors.length > 0) {
                let errorTitle = fieldName;
                if ((_a = fieldTranslations.value[fieldName]) === null || _a === void 0 ? void 0 : _a.label) {
                    errorTitle = fieldTranslations.value[fieldName].label;
                }
                else if ((_b = unref(customDefaultTranslationLabels)) === null || _b === void 0 ? void 0 : _b[fieldName]) {
                    errorTitle = t(unref(customDefaultTranslationLabels)[fieldName]);
                }
                errors.push({
                    errors: fieldErrors,
                    errorTitle
                });
            }
            return errors;
        }, []);
    }
    /**
     * Provide the old way of giving overview of form's errors
     * @returns object with field's array of error messages, mimicing how formError object was in appFormMixin
     */
    function legacyFormErrorMessages() {
        const formError = {};
        Object.keys(unref(initForm).vuelidateValidations).forEach(fieldName => {
            var _a;
            fieldName = mutateFieldName(fieldName);
            if (v$.value[formTrackerName][fieldName].$invalid == false ||
                v$.value[formTrackerName][fieldName].$dirty == false) {
                return;
            }
            const { appFormMixinI18MainKey } = legacy;
            const customLabel = (_a = unref(customDefaultTranslationLabels)) === null || _a === void 0 ? void 0 : _a[fieldName];
            const errors = v$.value[formTrackerName][fieldName].$errors.map(x => x.$message);
            if (errors.length > 0) {
                formError[fieldName] = {
                    // "this" here refers to this.formError[fieldName]
                    get: function () {
                        return this.errors;
                    },
                    errors: errors,
                    translatedFieldName: customLabel ? t(customLabel) : undefined,
                    i18nKey: appFormMixinI18MainKey
                        ? appFormMixinI18MainKey + `.${fieldName}.label`
                        : null
                };
            }
        });
        return formError;
    }
    return {
        v$,
        fieldDisabled,
        fieldErrorMessages,
        formErrorMessages,
        formValidate,
        asyncFormValidate,
        fieldTranslations,
        legacyFormErrorMessages
    };
}
export const BasicFormFieldFragment = gql `
  fragment _ on FormField {
    name
    labelTranslationKey
    label
    hintTranslationKey
    hint
  }
`;
